import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { MessageSquare, Plus, PenLine, Gavel, Phone, Mail, Users, FileQuestion } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/features/auth';
import { useInteractions } from '../hooks';
import { InteractionDialog } from './InteractionDialog';
import type { InteractionEntry, InteractionType, Matter } from '../types';

const TYPE_ICONS: Record<InteractionType, React.ComponentType<{ className?: string }>> = {
  COURT_HEARING: Gavel,
  MEETING: Users,
  CALL: Phone,
  EMAIL: Mail,
  OTHER: FileQuestion,
};

interface InteractionLogCardProps {
  matter: Matter;
}

/**
 * Case 360° (Lot 2) Stage 7 — the matter's Interaction Log. Lists entries
 * (drafts first, amber-highlighted), lets team members add an entry, and lets
 * the team complete auto-drafted entries (summary required to finalize).
 * For court-case matters ≥1 finalized entry satisfies the Gate 3 hard block.
 */
export function InteractionLogCard({ matter }: InteractionLogCardProps) {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { data: entries, isLoading } = useInteractions(matter?.id);

  const canWrite =
    hasAccess('matter', 'interaction') && matter?.phase !== 'CLOSED';

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<InteractionEntry | null>(null);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openComplete = (entry: InteractionEntry) => {
    setEditing(entry);
    setDialogOpen(true);
  };

  const drafts = entries?.filter((e) => e?.isDraft) ?? [];

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-slate-400" />
            {t('interaction.cardTitle')}
            {drafts.length > 0 && (
              <Badge className="border border-amber-300 bg-amber-50 text-amber-700">
                {t('interaction.unfilled', { count: drafts.length })}
              </Badge>
            )}
          </span>
          {canWrite && (
            <Button size="sm" variant="outline" onClick={openAdd}>
              <Plus className="mr-1 h-4 w-4" />
              {t('interaction.add')}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <p className="text-sm text-slate-500">{t('loading')}</p>
        ) : !entries?.length ? (
          <p className="text-sm text-slate-500">{t('interaction.empty')}</p>
        ) : (
          <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {entries.map((entry) => {
              const Icon = TYPE_ICONS[entry?.type ?? 'OTHER'] ?? FileQuestion;
              return (
                <li
                  key={entry.id}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    entry?.isDraft
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      <Icon className="h-4 w-4 text-slate-400" />
                      {t(`interaction.type.${entry?.type ?? 'OTHER'}`)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {entry?.date ? format(new Date(entry.date), 'dd MMM yyyy') : ''}
                    </span>
                  </div>
                  {entry?.isDraft ? (
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-xs text-amber-700">
                        {t('interaction.draftHint')}
                      </span>
                      {canWrite && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                          onClick={() => openComplete(entry)}
                        >
                          <PenLine className="mr-1 h-3.5 w-3.5" />
                          {t('interaction.complete')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="mt-1 whitespace-pre-wrap text-slate-600">
                        {entry?.summary}
                      </p>
                      {entry?.author?.name && (
                        <p className="mt-1 text-xs text-slate-400">
                          {entry.author.name}
                        </p>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <InteractionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        matter={matter}
        entry={editing}
      />
    </Card>
  );
}
